import {
  BulkDuplicateMediaHouseDocument,
  type BulkDuplicateMediaHouseMutation,
  type BulkDuplicateMediaHouseMutationVariables,
  type MediaHouseMedia,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiCopy, FiMove, FiTrash2, FiType, FiX } from "react-icons/fi";

interface FloatingActionBarProps {
  selectedItems: MediaHouseMedia[];
  selectedCount: number;
  mediaType: "image" | "video" | "audio";
  onClearSelection: () => void;
  onRename: (item: MediaHouseMedia) => void;
  onMove: (items: MediaHouseMedia[]) => void;
  onDelete: (items: MediaHouseMedia[]) => void;
}

export function FloatingActionBar({
  selectedItems,
  selectedCount,
  mediaType,
  onClearSelection,
  onRename,
  onMove,
  onDelete,
}: FloatingActionBarProps) {
  const [duplicating, setDuplicating] = useState(false);

  const getRefetchQuery = () => {
    if (mediaType === "image") return "GetMediaHouseImages";
    if (mediaType === "video") return "GetMediaHouseVideos";
    return "GetMediaHouseAudio";
  };

  const [bulkDuplicateMedia] = useMutation<
    BulkDuplicateMediaHouseMutation,
    BulkDuplicateMediaHouseMutationVariables
  >(BulkDuplicateMediaHouseDocument, {
    refetchQueries: [getRefetchQuery()],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const count = data.bulkDuplicateMediaHouse.length;
      toast.success(`Duplicated ${count} ${count === 1 ? "item" : "items"}`);
      onClearSelection();
      setDuplicating(false);
    },
    onError: (error) => {
      toast.error(`Failed to duplicate: ${error.message}`);
      setDuplicating(false);
    },
  });

  const isSingleSelection = selectedCount === 1;
  const firstItem = selectedItems[0] || null;

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      await bulkDuplicateMedia({ variables: { ids: selectedItems.map((item) => item.id) } });
    } finally {
      setDuplicating(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-5 left-1/2 z-40 -translate-x-1/2">
      <div
        className="flex items-center gap-1 px-3 py-2 rounded-2xl shadow-2xl"
        style={{
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(226,232,240,0.8)",
          boxShadow:
            "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(255,255,255,0.6) inset",
        }}
      >
        {/* Gradient top accent */}
        <div
          className="absolute top-0 left-6 right-6 h-px rounded-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(99,102,241,0.5), rgba(20,184,166,0.5), transparent)",
          }}
        />

        {/* Count + clear */}
        <div className="flex items-center gap-1.5 pr-3 border-r border-slate-200">
          <span
            className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
            style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}
          >
            {selectedCount}
          </span>
          <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
            {selectedCount === 1 ? "item" : "items"}
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            title="Clear selection"
            className="ml-0.5 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 pl-1">
          {/* Rename — single only */}
          {isSingleSelection && firstItem && (
            <ActionBtn
              onClick={() => onRename(firstItem)}
              icon={<FiType className="h-3.5 w-3.5" />}
              label="Rename"
            />
          )}

          <ActionBtn
            onClick={() => {
              onMove(selectedItems);
              onClearSelection();
            }}
            icon={<FiMove className="h-3.5 w-3.5" />}
            label="Move"
          />

          <ActionBtn
            onClick={handleDuplicate}
            disabled={duplicating}
            icon={
              duplicating ? (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
              ) : (
                <FiCopy className="h-3.5 w-3.5" />
              )
            }
            label={duplicating ? "..." : "Copy"}
          />

          {/* Delete — distinct danger colour */}
          <button
            type="button"
            onClick={() => onDelete(selectedItems)}
            title="Move to trash"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold ml-1
              text-error-600 bg-error-50 hover:bg-error-500 hover:text-white
              transition-all duration-150 active:scale-95"
          >
            <FiTrash2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Trash</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({
  onClick,
  icon,
  label,
  disabled,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium
        text-slate-600 hover:text-slate-900 hover:bg-slate-100
        transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
